import { Unit, UnitMetadata, UnitType, Building, Contact } from '../../src/app/shared/models/entity';
import { auditFields, createMany, cycle, FixtureBuilder, FixtureFactory, fixtureId } from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ SAMPLE POOLS
// -----------------------------------------------------------------------------------------------------

const UNIT_TYPES: readonly UnitType[] = ['Apartment', 'Apartment', 'Apartment', 'Commercial', 'Garage', 'Parking'];

const AREAS: readonly number[] = [48.5, 62, 74.2, 88, 95.5, 112];

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a Unit with realistic defaults. Every field can be overridden.
 *
 * The denormalized building and property fields default to empty strings — use `inBuilding()` on the
 * builder, or `withBuilding()` in the seed, to wire them consistently.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the sample rotation.
 * @returns {Unit} A fresh Unit instance.
 */
export const createUnit: FixtureFactory<Unit> = (overrides: Partial<Unit> = {}, index: number = 0): Unit => {
    const number: string = `${601 + index}`;
    const area: number = cycle(AREAS, index);

    return new Unit({
        id: fixtureId('unit', index),
        ...auditFields(index),
        name: `WE ${number}`,
        number,
        type: cycle(UNIT_TYPES, index),
        buildingId: '',
        buildingName: '',
        propertyId: '',
        propertyName: '',
        share: Math.round((area / 10) * 100) / 100,
        area,
        currentOwnerName: '',
        metadata: new UnitMetadata(),
        contacts: [],
        ...overrides,
    });
};

/**
 * Builds a list of Units.
 *
 * @param count - How many units to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {Unit[]} The built units.
 */
export function createUnits(count: number, overrides?: Partial<Unit> | ((index: number) => Partial<Unit>)): Unit[] {
    return createMany(createUnit, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_UNIT: Unit = createUnit();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** A commercial unit — no living area, larger share. */
export const UNIT_COMMERCIAL: Unit = createUnit({ type: 'Commercial', name: 'Gewerbe 1', area: 180, share: 22.5 }, 1);

/** A parking space — the minimal end of the range. */
export const UNIT_PARKING: Unit = createUnit({ type: 'Parking', name: 'TG 12', area: 0, share: 1.5 }, 2);

/** A unit that has not been assigned an owner yet. */
export const UNIT_WITHOUT_OWNER: Unit = createUnit({ currentOwnerName: '', contacts: [] }, 3);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class UnitBuilder extends FixtureBuilder<Unit> {
    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor(index: number = 0) {
        super(createUnit({}, index));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Places the unit in a building — wires both ids, both denormalized names and the relation
     * in a single call so the graph can never drift apart.
     *
     * @param building - The building the unit belongs to.
     * @param withRelation - Whether to attach the building instance itself. Off by default to keep the graph flat.
     * @returns {this} The builder, for chaining.
     */
    inBuilding(building: Building, withRelation: boolean = false): this {
        return this.with({
            buildingId: building.id,
            buildingName: building.name,
            propertyId: building.propertyId,
            propertyName: building.propertyName,
            building: withRelation ? building : undefined,
        });
    }

    /**
     * Sets the usage type of the unit.
     *
     * @param type - The usage type.
     * @returns {this} The builder, for chaining.
     */
    ofType(type: UnitType): this {
        return this.with({ type });
    }

    /**
     * Sets the co-ownership share and the living area of the unit.
     *
     * @param share - The MEA share.
     * @param area - The living area in square meters.
     * @returns {this} The builder, for chaining.
     */
    withShare(share: number, area: number): this {
        return this.with({ share, area });
    }

    /**
     * Assigns the owners of the unit and keeps `currentOwnerName` in sync with the first one.
     *
     * @param contacts - The owning contacts.
     * @returns {this} The builder, for chaining.
     */
    ownedBy(...contacts: Contact[]): this {
        return this.with({
            contacts,
            currentOwnerName: contacts.length > 0 ? contacts[0].name : '',
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ OVERRIDE METHODS
    // -----------------------------------------------------------------------------------------------------

    override build(): Unit {
        return new Unit(this.draft);
    }
}
